import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDocuments } from '@/api/domains/documents';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';
import { InputText } from 'primereact/inputtext';
import { Header } from '@/components/layout';
import styles from './DocumentsList.module.css';
import { BackButton } from '@/components/ui';

const DocumentsList = () => {
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({ skip: 0, limit: 12 });
  const [pageInput, setPageInput] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState('');

  const { data, error, isLoading } = useDocuments(
    searchQuery ? { skip: 0, limit: 500 } : pagination
  );

  const handlePageChange = (first: number, rows: number) => {
    setPagination({ skip: first, limit: rows });
  };

  const handleDocumentClick = (sourceUrl: string) => {
    navigate(`/documents/${encodeURIComponent(sourceUrl)}`);
  };

  const filteredDocuments = useMemo(() => {
    if (!data) return [];
    if (!searchQuery) {
      const start = pagination.skip;
      const end = start + pagination.limit;
      return data.documents.slice(start, end);
    }
    const query = searchQuery.trim().toLowerCase();
    return data.documents.filter((doc) => {
      return (
        doc.title.toLowerCase().includes(query) ||
        doc.source_url.toLowerCase().includes(query) ||
        doc.language.toLowerCase().includes(query)
      );
    });
  }, [data, searchQuery, pagination]);

  const totalFiltered = filteredDocuments.length;

  useEffect(() => {
    if (!data) return;
    const totalPages = Math.max(1, Math.ceil(data.total / pagination.limit));
    const currentPage = Math.min(
      totalPages,
      Math.floor(pagination.skip / pagination.limit) + 1
    );
    setPageInput(currentPage);
  }, [pagination.skip, pagination.limit, data]);

  const jumpToPage = (page: number) => {
    if (!data) return;
    const totalPages = Math.max(1, Math.ceil(data.total / pagination.limit));
    const p = Math.max(1, Math.min(totalPages, Math.floor(page)));
    handlePageChange((p - 1) * pagination.limit, pagination.limit);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;
  const totalPages = Math.max(
    1,
    Math.ceil((data?.total || 0) / pagination.limit)
  );

  return (
    <div className={styles.root}>
      <Header />
      <main className={styles.main}>
        <div className={styles.backButtonContainer}>
          <BackButton to="/" />
        </div>
        <section className={styles.pageHeader}>
          <div>
            <h1>Document Library</h1>
            <p className={styles.subtitle}>
              Browse and analyze regulatory documents
            </p>
          </div>
          <div className={styles.stats}>
            <span className={styles.totalCount}>
              <i className="pi pi-file" />
              {data?.total || 0} Documents
            </span>
          </div>
        </section>

        <section className={styles.toolbarRow}>
          <div className={styles.searchContainer}>
            <i className="pi pi-search" />
            <InputText
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                handlePageChange(0, pagination.limit);
              }}
              placeholder="Search by title, language, or source..."
              className={styles.searchInput}
            />
            {searchQuery && (
              <Button
                icon="pi pi-times"
                rounded
                text
                size="small"
                onClick={() => {
                  setSearchQuery('');
                  handlePageChange(0, pagination.limit);
                }}
                className={styles.clearButton}
                aria-label="Clear search"
              />
            )}
          </div>
        </section>

        {error && (
          <div className={styles.errorBox}>
            <i className="pi pi-exclamation-triangle" />
            <div>
              <h3>Failed to load documents</h3>
              <p>{error.message}</p>
            </div>
          </div>
        )}

        {isLoading && (
          <div className={styles.documentsGrid}>
            {[...Array(6)].map((_, i) => (
              <Card key={i} className={styles.documentCard}>
                <Skeleton width="100%" height="2rem" className="mb-2" />
                <Skeleton width="80%" height="1rem" className="mb-3" />
                <Skeleton width="60%" height="1rem" />
              </Card>
            ))}
          </div>
        )}

        {!isLoading && data && filteredDocuments.length > 0 && (
          <>
            <div className={styles.documentsGrid}>
              {filteredDocuments.map((doc) => (
                <Card
                  key={doc.source_url}
                  className={styles.documentCard}
                  onClick={() => handleDocumentClick(doc.source_url)}
                >
                  <div className={styles.cardHeader}>
                    <div className={styles.languageBadge}>{doc.language}</div>
                    <i className="pi pi-external-link" />
                  </div>

                  <h3 className={styles.documentTitle}>{doc.title}</h3>

                  <div className={styles.cardMeta}>
                    <div className={styles.metaItem}>
                      <i className="pi pi-calendar" />
                      <span>{formatDate(doc.published_date)}</span>
                    </div>
                  </div>

                  <Button
                    label="View Details"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    className={styles.viewButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDocumentClick(doc.source_url);
                    }}
                  />
                </Card>
              ))}
            </div>

            {data.total > pagination.limit && (
              <div className={styles.paginationBar}>
                <div className={styles.paginationInfo}>
                  <span>
                    Showing{' '}
                    <strong>
                      {pagination.skip + 1}–
                      {Math.min(pagination.skip + pagination.limit, data.total)}
                    </strong>{' '}
                    of <strong>{data.total}</strong>
                  </span>
                  <span className={styles.paginationDivider}>•</span>
                  <span>
                    Page <strong>{currentPage}</strong> of{' '}
                    <strong>{totalPages}</strong>
                  </span>
                  {searchQuery && (
                    <>
                      <span className={styles.paginationDivider}>•</span>
                      <span>
                        Filtered: <strong>{totalFiltered}</strong>
                      </span>
                    </>
                  )}
                </div>

                <div className={styles.paginationActions}>
                  <Button
                    icon="pi pi-angle-left"
                    className={styles.paginationIconButton}
                    outlined
                    onClick={() => jumpToPage(currentPage - 1)}
                    disabled={currentPage <= 1}
                    aria-label="Previous page"
                  />

                  <div className={styles.pageJump}>
                    <span className={styles.pageJumpLabel}>Jump to</span>
                    <input
                      type="number"
                      min={1}
                      max={totalPages}
                      value={pageInput}
                      onChange={(e) => setPageInput(Number(e.target.value))}
                      onBlur={() => jumpToPage(pageInput)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          jumpToPage(pageInput);
                        }
                      }}
                      className={styles.pageInput}
                      aria-label="Jump to page"
                    />
                  </div>

                  <Button
                    icon="pi pi-angle-right"
                    className={styles.paginationIconButton}
                    outlined
                    onClick={() => jumpToPage(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    aria-label="Next page"
                  />
                </div>
              </div>
            )}
          </>
        )}

        {!isLoading && data && filteredDocuments.length === 0 && (
          <div className={styles.emptyState}>
            <i className="pi pi-inbox" />
            <h3>No documents found</h3>
            <p>No documents match your search. Try a different keyword.</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default DocumentsList;
